import React, { useState } from 'react';
import { ACTIVE_GREEN, ACTIVE_GREEN_BG } from '../../constants/statusColors';
import {
  Paper,
  Box,
  Typography,
  List,
  ListItem,
  Avatar,
  Chip,
  IconButton,
  Collapse,
  Button,
  TextField,
  MenuItem,
  Grid,
  Divider,
  Tooltip,
} from '@mui/material';
import {
  ExpandMoreOutlined as ExpandMoreIcon,
  ExpandLessOutlined as ExpandLessIcon,
  AddOutlined as AddIcon,
  EditOutlined as EditIcon,
  DeleteOutlined as DeleteIcon,
  UploadFileOutlined as UploadIcon,
  DownloadOutlined as DownloadIcon,
  VisibilityOutlined as ViewIcon,
  AssignmentIndOutlined as AssignIcon,
  BlockOutlined as DeactivateIcon,
  CheckCircleOutlined as ApproveIcon,
  FileDownloadOutlined as ExportIcon,
  FilterListOutlined as FilterIcon,
  FilterAltOff as ClearFiltersIcon,
} from '@mui/icons-material';

const AuditLogComponent = ({ auditLogs = [], title = 'Audit Log' }) => {
  const [expandedLogs, setExpandedLogs] = useState({});
  const [filters, setFilters] = useState({
    actionType: '',
    section: '',
    dateFrom: '',
    dateTo: '',
  });

  // Get action icon
  const getActionIcon = (actionType) => {
    switch (actionType?.toLowerCase()) {
      case 'create':
      case 'add_shareholder':
        return <AddIcon fontSize="small" />;
      case 'update':
      case 'update_shareholding':
        return <EditIcon fontSize="small" />;
      case 'delete':
      case 'remove_shareholder':
        return <DeleteIcon fontSize="small" />;
      case 'upload':
        return <UploadIcon fontSize="small" />;
      case 'download':
        return <DownloadIcon fontSize="small" />;
      case 'view':
        return <ViewIcon fontSize="small" />;
      case 'assign':
        return <AssignIcon fontSize="small" />;
      case 'deactivate':
        return <DeactivateIcon fontSize="small" />;
      case 'approve':
        return <ApproveIcon fontSize="small" />;
      default:
        return <EditIcon fontSize="small" />;
    }
  };

  // Get action color
  const getActionColor = (actionType) => {
    switch (actionType?.toLowerCase()) {
      case 'create':
      case 'add_shareholder':
      case 'upload':
      case 'approve':
        return { bg: ACTIVE_GREEN_BG, color: ACTIVE_GREEN, border: ACTIVE_GREEN }; // Green
      case 'update':
      case 'update_shareholding':
      case 'assign':
        return { bg: '#E3F2FD', color: '#1565C0', border: '#2196F3' }; // Blue
      case 'delete':
      case 'remove_shareholder':
      case 'deactivate':
        return { bg: '#FFEBEE', color: '#C62828', border: '#F44336' }; // Red
      case 'view':
      case 'download':
      case 'export':
        return { bg: '#F5F5F5', color: '#616161', border: '#9E9E9E' }; // Gray
      default:
        return { bg: '#FFF3E0', color: '#E65100', border: '#FF9800' }; // Orange
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = new Date(timestamp);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });
  };

  const formatDateTime = (timestamp) => {
    return `${formatDate(timestamp)} at ${formatTime(timestamp)}`;
  };

  const toggleExpanded = (logId) => {
    setExpandedLogs((prev) => ({
      ...prev,
      [logId]: !prev[logId],
    }));
  };

  const handleFilterChange = (name, value) => {
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const clearFilters = () => {
    setFilters({
      actionType: '',
      section: '',
      dateFrom: '',
      dateTo: '',
    });
  };

  // Apply filters
  const filteredLogs = auditLogs.filter((log) => {
    if (filters.actionType && log.actionType !== filters.actionType) return false;
    if (filters.section && log.section !== filters.section) return false;
    if (filters.dateFrom && new Date(log.changedAt) < new Date(filters.dateFrom)) return false;
    if (filters.dateTo && new Date(log.changedAt) > new Date(filters.dateTo)) return false;
    return true;
  });

  // Group logs by date
  const groupedLogs = filteredLogs.reduce((groups, log) => {
    const date = formatDate(log.changedAt);
    if (!groups[date]) {
      groups[date] = [];
    }
    groups[date].push(log);
    return groups;
  }, {});

  const renderValueComparison = (log) => {
    const colors = getActionColor(log.actionType);
    
    return (
      <Box sx={{ mt: 2, p: 2, bgcolor: '#FAFAFA', borderRadius: 1 }}>
        <Grid container spacing={2}>
          {log.oldValue !== null && log.oldValue !== undefined && (
            <Grid item xs={12} md={6}>
              <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 600, textTransform: 'uppercase' }}>
                Before
              </Typography>
              <Paper sx={{ p: 2, mt: 1, bgcolor: '#FFEBEE', border: '1px solid #FFCDD2' }}>
                <Typography variant="body2" sx={{ fontFamily: 'monospace', whiteSpace: 'pre-wrap' }}>
                  {typeof log.oldValue === 'object' ? JSON.stringify(log.oldValue, null, 2) : String(log.oldValue)}
                </Typography>
              </Paper>
            </Grid>
          )}
          {log.newValue !== null && log.newValue !== undefined && (
            <Grid item xs={12} md={log.oldValue ? 6 : 12}>
              <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 600, textTransform: 'uppercase' }}>
                {log.oldValue ? 'After' : 'New Value'}
              </Typography>
              <Paper sx={{ p: 2, mt: 1, bgcolor: ACTIVE_GREEN_BG, border: '1px solid rgba(127, 219, 131, 0.45)' }}>
                <Typography variant="body2" sx={{ fontFamily: 'monospace', whiteSpace: 'pre-wrap' }}>
                  {typeof log.newValue === 'object' ? JSON.stringify(log.newValue, null, 2) : String(log.newValue)}
                </Typography>
              </Paper>
            </Grid>
          )}
        </Grid>
      </Box>
    );
  };

  if (!auditLogs || auditLogs.length === 0) {
    return (
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" sx={{ mb: 3 }}>
          {title}
        </Typography>
        <Box textAlign="center" py={6}>
          <ExportIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
          <Typography variant="body1" color="textSecondary" gutterBottom>
            No audit logs recorded
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Changes will be tracked automatically
          </Typography>
        </Box>
      </Paper>
    );
  }

  return (
    <Paper sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h6">{title}</Typography>
          <Typography variant="caption" color="textSecondary">
            {filteredLogs.length} {filteredLogs.length === 1 ? 'entry' : 'entries'}
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<ExportIcon />}
          size="small"
        >
          Export Logs
        </Button>
      </Box>

      {/* Filters */}
      <Box sx={{ mb: 3, p: 2, bgcolor: '#F5F5F5', borderRadius: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <FilterIcon fontSize="small" color="action" />
          <Typography variant="subtitle2" fontWeight={600}>
            Filters
          </Typography>
        </Box>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              select
              fullWidth
              size="small"
              label="Action Type"
              value={filters.actionType}
              onChange={(e) => handleFilterChange('actionType', e.target.value)}
            >
              <MenuItem value="">All Actions</MenuItem>
              <MenuItem value="create">Create</MenuItem>
              <MenuItem value="update">Update</MenuItem>
              <MenuItem value="delete">Delete</MenuItem>
              <MenuItem value="upload">Upload</MenuItem>
              <MenuItem value="assign">Assign</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              select
              fullWidth
              size="small"
              label="Section"
              value={filters.section}
              onChange={(e) => handleFilterChange('section', e.target.value)}
            >
              <MenuItem value="">All Sections</MenuItem>
              <MenuItem value="Personal Information">Personal Information</MenuItem>
              <MenuItem value="Contact Information">Contact Information</MenuItem>
              <MenuItem value="Documents">Documents</MenuItem>
              <MenuItem value="Shareholding Structure">Shareholding</MenuItem>
              <MenuItem value="Company Officers">Officers</MenuItem>
              <MenuItem value="Committee Memberships">Committees</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              type="date"
              fullWidth
              size="small"
              label="From Date"
              value={filters.dateFrom}
              onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              type="date"
              fullWidth
              size="small"
              label="To Date"
              value={filters.dateTo}
              onChange={(e) => handleFilterChange('dateTo', e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs="auto">
            <Tooltip title="Clear Filters">
              <IconButton
                onClick={clearFilters}
                color="primary"
                sx={{ mt: 0.5 }}
              >
                <ClearFiltersIcon />
              </IconButton>
            </Tooltip>
          </Grid>
        </Grid>
      </Box>

      {/* Timeline */}
      {filteredLogs.length === 0 ? (
        <Box textAlign="center" py={4}>
          <Typography variant="body2" color="textSecondary">
            No logs match the selected filters
          </Typography>
        </Box>
      ) : (
        Object.entries(groupedLogs).map(([date, logs]) => (
          <Box key={date} sx={{ mb: 3 }}>
            {/* Date Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  borderRadius: '50%',
                  bgcolor: '#D32F2F',
                  mr: 2,
                }}
              />
              <Typography variant="subtitle2" fontWeight={600} color="textSecondary">
                {date}
              </Typography>
              <Divider sx={{ flex: 1, ml: 2 }} />
            </Box>

            {/* Logs for this date */}
            <List sx={{ p: 0, pl: 3, borderLeft: '2px solid #E0E0E0' }}>
              {logs.map((log, index) => {
                const colors = getActionColor(log.actionType);
                const isExpanded = expandedLogs[log.auditId];

                return (
                  <ListItem
                    key={log.auditId}
                    sx={{
                      px: 0,
                      py: 2,
                      pl: 2,
                      flexDirection: 'column',
                      alignItems: 'flex-start',
                      position: 'relative',
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        left: -7,
                        top: 24,
                        width: 12,
                        height: 12,
                        borderRadius: '50%',
                        bgcolor: colors.border,
                        border: '2px solid white',
                      },
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, width: '100%' }}>
                      {/* User Avatar */}
                      <Avatar
                        sx={{
                          width: 40,
                          height: 40,
                          bgcolor: colors.bg,
                          color: colors.color,
                          fontSize: '0.875rem',
                        }}
                      >
                        {log.changedByName?.split(' ').map(n => n[0]).join('') || '?'}
                      </Avatar>

                      {/* Log Content */}
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        {/* Time and User */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                          <Typography variant="caption" color="textSecondary" fontWeight={600}>
                            {formatTime(log.changedAt)}
                          </Typography>
                          <Typography variant="body2" fontWeight={500}>
                            {log.changedByName}
                          </Typography>
                          <Chip
                            label={log.changedByRole}
                            size="small"
                            sx={{
                              height: 18,
                              fontSize: '0.65rem',
                              bgcolor: '#F5F5F5',
                              color: '#757575',
                            }}
                          />
                        </Box>

                        {/* Action Description */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                          <Chip
                            icon={getActionIcon(log.actionType)}
                            label={log.actionType.replace(/_/g, ' ')}
                            size="small"
                            sx={{
                              bgcolor: colors.bg,
                              color: colors.color,
                              fontWeight: 600,
                              fontSize: '0.7rem',
                              height: 24,
                              textTransform: 'uppercase',
                              '& .MuiChip-icon': {
                                color: colors.color,
                              },
                            }}
                          />
                          <Typography variant="body2">
                            {log.section && (
                              <Chip
                                label={log.section}
                                size="small"
                                sx={{
                                  height: 20,
                                  fontSize: '0.7rem',
                                  bgcolor: 'white',
                                  border: '1px solid #E0E0E0',
                                }}
                              />
                            )}
                          </Typography>
                        </Box>

                        {/* Change Summary */}
                        {log.fieldLabel && (
                          <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                            <strong>{log.fieldLabel}:</strong>{' '}
                            {log.oldValue && typeof log.oldValue !== 'object' && (
                              <Box component="span" sx={{ textDecoration: 'line-through', color: '#D32F2F' }}>
                                {String(log.oldValue)}
                              </Box>
                            )}
                            {log.oldValue && log.newValue && ' → '}
                            {log.newValue && typeof log.newValue !== 'object' && (
                              <Box component="span" sx={{ color: ACTIVE_GREEN, fontWeight: 500 }}>
                                {String(log.newValue)}
                              </Box>
                            )}
                          </Typography>
                        )}

                        {/* Entity Name */}
                        {log.entityName && (
                          <Typography variant="caption" color="textSecondary">
                            Entity: {log.entityName}
                          </Typography>
                        )}

                        {/* Reason */}
                        {log.changeReason && (
                          <Box
                            sx={{
                              mt: 1,
                              p: 1.5,
                              bgcolor: '#FFF3E0',
                              borderLeft: '3px solid #FF9800',
                              borderRadius: 1,
                            }}
                          >
                            <Typography variant="caption" color="textSecondary" fontWeight={600}>
                              Reason:
                            </Typography>
                            <Typography variant="body2" sx={{ mt: 0.5 }}>
                              {log.changeReason}
                            </Typography>
                          </Box>
                        )}

                        {/* View Details Button */}
                        {(log.oldValue || log.newValue || log.metadata) && (
                          <Button
                            size="small"
                            onClick={() => toggleExpanded(log.auditId)}
                            endIcon={isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                            sx={{ mt: 1 }}
                          >
                            {isExpanded ? 'Hide Details' : 'View Details'}
                          </Button>
                        )}

                        {/* Expanded Details */}
                        <Collapse in={isExpanded}>
                          <Box sx={{ mt: 2, p: 2, bgcolor: 'white', border: '1px solid #E0E0E0', borderRadius: 1 }}>
                            {/* Full Change Details */}
                            {(log.oldValue || log.newValue) && renderValueComparison(log)}

                            {/* Metadata */}
                            {log.metadata && (
                              <Box sx={{ mt: 2 }}>
                                <Typography variant="caption" color="textSecondary" fontWeight={600}>
                                  Additional Details:
                                </Typography>
                                <Box sx={{ mt: 1, p: 1.5, bgcolor: '#F5F5F5', borderRadius: 1, fontSize: '0.75rem' }}>
                                  <pre style={{ margin: 0, fontFamily: 'monospace', whiteSpace: 'pre-wrap' }}>
                                    {JSON.stringify(log.metadata, null, 2)}
                                  </pre>
                                </Box>
                              </Box>
                            )}

                            {/* Technical Info */}
                            <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid #E0E0E0' }}>
                              <Grid container spacing={2}>
                                <Grid item xs={6}>
                                  <Typography variant="caption" color="textSecondary">
                                    Audit ID
                                  </Typography>
                                  <Typography variant="body2">#{log.auditId}</Typography>
                                </Grid>
                                <Grid item xs={6}>
                                  <Typography variant="caption" color="textSecondary">
                                    Session ID
                                  </Typography>
                                  <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                                    {log.sessionId || 'N/A'}
                                  </Typography>
                                </Grid>
                                <Grid item xs={6}>
                                  <Typography variant="caption" color="textSecondary">
                                    IP Address
                                  </Typography>
                                  <Typography variant="body2">{log.ipAddress || 'N/A'}</Typography>
                                </Grid>
                                <Grid item xs={6}>
                                  <Typography variant="caption" color="textSecondary">
                                    Timestamp
                                  </Typography>
                                  <Typography variant="body2">{formatDateTime(log.changedAt)}</Typography>
                                </Grid>
                              </Grid>
                            </Box>
                          </Box>
                        </Collapse>
                      </Box>
                    </Box>
                  </ListItem>
                );
              })}
            </List>
          </Box>
        ))
      )}
    </Paper>
  );
};

export default AuditLogComponent;
